import {
	useConfigs,
	useSCIMConfigurationStatus,
	useSSOConfigurationStatus,
	useXAAConfigurationStatus
} from 'api/configurations';
import {
	useMethodGuide,
	useScimMethodGuide,
	useXaaMethodGuide
} from 'api/guide';
import { GENERIC_IDP_ID, GuideType, Method, SCIMMethod } from 'consts';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { loadPrevState, storeState } from 'utils/storage';
import { StorageK } from 'keys';
import { useCurrentMethod, useIdPConfigContext } from './IdPConfigContext';

type IGuideStepsProgress = Record<
	string,
	Record<string, Record<string, Record<string, boolean>>>
>;

type IGuideProgression = {
	ssoTested?: boolean;
	isStepCompleted: (stepNum: number) => boolean;
	setStepCompleted: (stepNum: number) => void;
	totalSteps: number | null;
	completedSteps: number | null;
};

interface IGuideProgressContextValue {
	guidesProgress: IGuideStepsProgress;
	setGuidesProgress: React.Dispatch<React.SetStateAction<IGuideStepsProgress>>;
}

const GuideProgressContext = createContext<
	IGuideProgressContextValue | undefined
>(undefined);

interface IGuideProgressContextProviderProps {
	children: React.ReactNode;
	skipLoadStorage?: boolean;
}

const GuideProgressContextProvider: React.FC<
	IGuideProgressContextProviderProps
> = ({ children, skipLoadStorage = false }) => {
	const [guidesProgress, setGuidesProgress] = useState<IGuideStepsProgress>({});

	const value = useMemo(
		() => ({ guidesProgress, setGuidesProgress }),
		[guidesProgress]
	);

	return (
		<GuideProgressContext.Provider value={value}>
			{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
			{!skipLoadStorage && <StorageSync />}
			{children}
		</GuideProgressContext.Provider>
	);
};

const useGuideProgressContext = (): IGuideProgressContextValue => {
	const context = useContext(GuideProgressContext);
	if (!context) throw new Error('GuideProgressContextProvider is missing');
	return context;
};

const STORE_VERSION = 1;

const StorageSync = () => {
	const [storageLoaded, setStorageLoaded] = useState(false);
	const { guidesProgress, setGuidesProgress } = useGuideProgressContext();

	const { data: configsData } = useConfigs();

	useEffect(() => {
		if (!configsData || storageLoaded) {
			return;
		}

		const prevGuideProgress = loadPrevState<IGuideStepsProgress>(
			StorageK.Progress,
			configsData.tenant.id,
			configsData.currentSSOId
		);

		setStorageLoaded(true);
		if (prevGuideProgress) {
			setGuidesProgress(prevGuideProgress);
		}
	}, [configsData, setGuidesProgress, setStorageLoaded, storageLoaded]);

	useEffect(() => {
		if (!configsData || !storageLoaded) {
			return;
		}

		storeState(
			StorageK.Progress,
			configsData.tenant.id,
			configsData.currentSSOId,
			STORE_VERSION,
			guidesProgress
		);
	}, [configsData, guidesProgress, storageLoaded]);

	return null;
};

const useMethodStepCompletion = (
	guideType: GuideType,
	idpId: string | null,
	method: Method | SCIMMethod | null
) => {
	const { guidesProgress, setGuidesProgress } = useGuideProgressContext();
	const { data: ssoConfigurationStatusData } = useSSOConfigurationStatus();
	const { data: scimConfigurationStatusData } = useSCIMConfigurationStatus();
	const { data: xaaConfigurationStatusData } = useXAAConfigurationStatus();

	const setStepCompleted = useCallback(
		(stepNum: number) => {
			/* istanbul ignore if: no need to test this */
			if (!idpId || !method) {
				return;
			}

			setGuidesProgress((currentGuideProgress) => ({
				...currentGuideProgress,
				[guideType]: {
					...(currentGuideProgress[guideType] || {}),
					[idpId]: {
						...(currentGuideProgress[guideType]?.[idpId] || {}),
						[method]: {
							...(currentGuideProgress[guideType]?.[idpId]?.[method] || {}),
							[stepNum]: true
						}
					}
				}
			}));
		},
		[guideType, idpId, method, setGuidesProgress]
	);

	const isStepCompleted = useCallback(
		(stepNum: number) => {
			/* istanbul ignore if */
			if (!guideType || !idpId || !method) {
				return false;
			}
			if (
				guideType === GuideType.SSO &&
				ssoConfigurationStatusData?.ssoConfigured &&
				idpId === ssoConfigurationStatusData?.idp?.id &&
				method === ssoConfigurationStatusData.method
			) {
				return true;
			}
			if (
				guideType === GuideType.SCIM &&
				scimConfigurationStatusData?.scimConfigured &&
				idpId === scimConfigurationStatusData?.idp?.id
			) {
				return true;
			}
			if (
				guideType === GuideType.XAA &&
				xaaConfigurationStatusData?.xaaConfigured
			) {
				return true;
			}
			return guidesProgress[guideType]?.[idpId]?.[method]?.[stepNum] || false;
		},
		[
			guideType,
			guidesProgress,
			idpId,
			method,
			scimConfigurationStatusData,
			ssoConfigurationStatusData,
			xaaConfigurationStatusData
		]
	);

	return useMemo(
		() => ({
			isStepCompleted,
			setStepCompleted
		}),
		[isStepCompleted, setStepCompleted]
	);
};

const useSSOGuideProgress = (): IGuideProgression => {
	const { idpID, method } = useIdPConfigContext();

	const { isStepCompleted, setStepCompleted } = useMethodStepCompletion(
		GuideType.SSO,
		idpID,
		method
	);
	const { data: ssoConfigurationStatusData } = useSSOConfigurationStatus();
	const { data: methodGuide } = useMethodGuide(idpID, method);

	// +1 for test, +1 for select IdP step
	const totalSteps = methodGuide?.guide.length
		? methodGuide.guide.length + 2
		: null;

	const ssoTested = useMemo(
		() =>
			Boolean(
				ssoConfigurationStatusData?.idp?.id === idpID &&
				ssoConfigurationStatusData.method === method &&
				ssoConfigurationStatusData.ssoLastSuccessTestTime
			),
		[ssoConfigurationStatusData, idpID, method]
	);

	const completedSteps = useMemo(() => {
		if (!idpID || !method || !methodGuide) {
			return null;
		}

		const completedGuideSteps = Array(methodGuide.guide.length)
			.fill(0)
			.reduce(
				(agg, val, index) => agg + (isStepCompleted(index + 1) ? 1 : 0),
				0
			);

		return (
			completedGuideSteps +
			Number(ssoTested) +
			// + 1 for select idp
			1
		);
	}, [idpID, isStepCompleted, method, methodGuide, ssoTested]);

	return useMemo(
		() => ({
			ssoTested,
			isStepCompleted,
			setStepCompleted,
			totalSteps,
			completedSteps
		}),
		[completedSteps, isStepCompleted, setStepCompleted, ssoTested, totalSteps]
	);
};

const useSCIMGuideProgress = (): IGuideProgression => {
	const { scimIdpID, scimMethod } = useIdPConfigContext();
	const method = useCurrentMethod();

	const { isStepCompleted, setStepCompleted } = useMethodStepCompletion(
		GuideType.SCIM,
		scimIdpID,
		method
	);

	const { data: scimGuide } = useScimMethodGuide(scimIdpID, scimMethod);

	// +1 for select IdP step
	const totalSteps = scimGuide?.guide.length
		? scimGuide.guide.length + 1
		: null;

	const completedSteps = useMemo(() => {
		if (!scimIdpID || !method || !scimGuide) {
			return null;
		}

		const completedGuideSteps = Array(scimGuide.guide.length)
			.fill(0)
			.reduce(
				(agg, val, index) => agg + (isStepCompleted(index + 1) ? 1 : 0),
				0
			);

		return (
			completedGuideSteps +
			// + 1 for select idp
			1
		);
	}, [scimIdpID, isStepCompleted, method, scimGuide]);

	return useMemo(
		() => ({
			isStepCompleted,
			setStepCompleted,
			totalSteps,
			completedSteps
		}),
		[completedSteps, isStepCompleted, setStepCompleted, totalSteps]
	);
};

const useXAAGuideProgress = (): IGuideProgression => {
	const { xaaIdpID } = useIdPConfigContext();
	const { isStepCompleted, setStepCompleted } = useMethodStepCompletion(
		GuideType.XAA,
		xaaIdpID || GENERIC_IDP_ID,
		Method.Xaa
	);

	const { data: xaaGuide } = useXaaMethodGuide();

	// XAA has no select-idp step, so total is just the guide steps.
	const totalSteps = xaaGuide?.guide.length ? xaaGuide.guide.length : null;

	const completedSteps = useMemo(() => {
		if (!xaaGuide) {
			return null;
		}

		return Array(xaaGuide.guide.length)
			.fill(0)
			.reduce(
				(agg, val, index) => agg + (isStepCompleted(index + 1) ? 1 : 0),
				0
			);
	}, [isStepCompleted, xaaGuide]);

	return useMemo(
		() => ({
			isStepCompleted,
			setStepCompleted,
			totalSteps,
			completedSteps
		}),
		[completedSteps, isStepCompleted, setStepCompleted, totalSteps]
	);
};

export {
	GuideProgressContextProvider,
	useSCIMGuideProgress,
	useSSOGuideProgress,
	useXAAGuideProgress
};
